import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppRole, OrganizationPermissions } from '../models/organization.model';
import { AuthService } from './auth.service';

const NONE: OrganizationPermissions = {
  manageOrganization: false,
  manageUsers: false,
  manageCompanyProfile: false,
  manageNotifications: false,
  viewSubscription: false,
  managePrivacy: false,
};

/**
 * Central mock definition of what each role may do. Components ask for a permission,
 * never for a role. Later the real permissions come from the backend.
 * SUPER_ADMIN is handled by the separate /admin portal and has no customer permissions here.
 */
export const ROLE_PERMISSIONS: Record<AppRole, OrganizationPermissions> = {
  ORG_ADMIN: {
    manageOrganization: true,
    manageUsers: true,
    manageCompanyProfile: true,
    manageNotifications: true,
    viewSubscription: true,
    managePrivacy: true,
  },
  ORG_USER: NONE,
  SUPER_ADMIN: NONE,
};

export const ROLE_LABELS: Record<AppRole, string> = {
  ORG_USER: 'Benutzer',
  ORG_ADMIN: 'Organisationsadmin',
  SUPER_ADMIN: 'Vergabe-Suche Admin',
};

/**
 * The role the UI works with.
 *
 * Real authentication mode (environment.requireAuth): the role comes from GET /api/v1/me and nothing else.
 * Prototype mode: a DEVELOPMENT ONLY switcher lets the mock UI show the three usage levels. That switcher is
 * not authentication, is not persisted, and has no effect on the backend, which decides authorization itself.
 */
@Injectable({ providedIn: 'root' })
export class RoleService {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly current = signal<AppRole>('ORG_ADMIN');
  /** The customer role to return to when leaving the /admin portal. */
  private lastCustomerRole: AppRole = 'ORG_ADMIN';

  readonly role = computed<AppRole>(() => (this.auth.enabled ? this.serverRole() : this.current()));
  readonly permissions = computed(() => ROLE_PERMISSIONS[this.role()]);

  private serverRole(): AppRole {
    return this.auth.me()?.role ?? 'ORG_USER';
  }

  constructor() {
    if (this.auth.enabled) return; // The server decides. No mock role logic.
    // Being inside /admin means SUPER_ADMIN. Leaving it restores the previous customer role.
    this.router.events.subscribe((e) => {
      if (!(e instanceof NavigationEnd)) return;
      const inAdmin = e.urlAfterRedirects.startsWith('/admin');
      if (inAdmin && this.current() !== 'SUPER_ADMIN') this.current.set('SUPER_ADMIN');
      if (!inAdmin && this.current() === 'SUPER_ADMIN') this.current.set(this.lastCustomerRole);
    });
  }

  can(permission: keyof OrganizationPermissions): boolean {
    return this.permissions()[permission];
  }

  /** Development switcher. Ignored in real authentication mode. */
  setRole(role: AppRole): void {
    if (this.auth.enabled) return;
    if (role !== 'SUPER_ADMIN') this.lastCustomerRole = role;
    this.current.set(role);
    if (role === 'SUPER_ADMIN') {
      this.router.navigateByUrl('/admin');
    } else if (this.router.url.startsWith('/organization') && !this.can('manageOrganization')) {
      // Convenience only: the page would otherwise stay open after losing access.
      this.router.navigateByUrl('/discover');
    }
  }
}
